import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Save, FileDown, Plus, Trash2, Edit, Check, X,
  User, Phone, MapPin, Heart, Camera, PawPrint, AlertTriangle,
  Home, Car, FileText, Shield, Clock, Navigation, Users,
  Stethoscope, Building, Map, ChevronRight, CheckCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

// Helper function to normalize special characters for PDF
const normalizeForPDF = (text) => {
  if (!text) return '';
  
  let result = String(text);
  
  // Spanish characters to ASCII
  result = result
    .replace(/á/g, 'a').replace(/Á/g, 'A')
    .replace(/é/g, 'e').replace(/É/g, 'E')
    .replace(/í/g, 'i').replace(/Í/g, 'I')
    .replace(/ó/g, 'o').replace(/Ó/g, 'O')
    .replace(/ú/g, 'u').replace(/Ú/g, 'U')
    .replace(/ñ/g, 'n').replace(/Ñ/g, 'N')
    .replace(/ü/g, 'u').replace(/Ü/g, 'U')
    .replace(/¿/g, '').replace(/¡/g, '')
    .replace(/–/g, '-').replace(/—/g, '-')
    .replace(/"/g, '"').replace(/"/g, '"')
    .replace(/'/g, "'").replace(/'/g, "'")
    .replace(/…/g, '...')
    .replace(/°/g, 'o')
    .replace(/€/g, 'EUR')
    .replace(/•/g, '-');
  
  // Remove emojis and non-ASCII characters
  let cleaned = '';
  for (let i = 0; i < result.length; i++) {
    const code = result.charCodeAt(i);
    if (code <= 127) {
      cleaned += result[i];
    }
  }
  
  return cleaned;
};

// ============================================
// TIPOS DE ANIMAL
// ============================================
const ANIMAL_TYPES = [
  { id: "perro", label: "Perro", icon: "" },
  { id: "gato", label: "Gato", icon: "" },
  { id: "ave", label: "Ave", icon: "" },
  { id: "conejo", label: "Conejo/Roedor", icon: "" },
  { id: "reptil", label: "Reptil", icon: "" },
  { id: "pez", label: "Peces", icon: "" },
  { id: "caballo", label: "Caballo", icon: "" },
  { id: "otro", label: "Otro", icon: "" },
];

// ============================================
// SECCIONES DEL PLAN
// ============================================
const PLAN_SECTIONS = [
  { id: "animales", label: "Mis Animales", icon: PawPrint, color: "#f59e0b" },
  { id: "contactos", label: "Contactos", icon: Phone, color: "#3b82f6" },
  { id: "reuniones", label: "Puntos de Encuentro", icon: MapPin, color: "#22c55e" },
  { id: "evacuacion", label: "Plan de Evacuación", icon: Car, color: "#ef4444" },
  { id: "documentos", label: "Documentos", icon: FileText, color: "#8b5cf6" },
];

// ============================================
// CHECKLIST DE DOCUMENTOS IMPORTANTES
// ============================================
const DOCUMENTS_CHECKLIST = [
  { id: "cartilla", name: "Cartilla sanitaria de cada animal", essential: true },
  { id: "vacunas", name: "Certificados de vacunación", essential: true },
  { id: "microchip", name: "Número de microchip anotado", essential: true },
  { id: "fotos", name: "Fotos recientes de cada animal (impresas)", essential: true },
  { id: "recetas", name: "Recetas de medicamentos habituales", essential: true },
  { id: "seguro", name: "Póliza de seguro de animales", essential: false },
  { id: "dni", name: "Copia del DNI del tutor/a", essential: false },
  { id: "licencia", name: "Licencia PPP (si aplica)", essential: false },
  { id: "pasaporte", name: "Pasaporte del animal (viajes)", essential: false },
  { id: "contrato_adopcion", name: "Contrato de adopción/compra", essential: false },
];

// ============================================
// TELÉFONOS DE EMERGENCIA POR DEFECTO
// ============================================
const DEFAULT_EMERGENCY_CONTACTS = [
  { type: "emergencia", name: "Emergencias", phone: "112", notes: "Emergencias generales" },
  { type: "sanitario", name: "Emergencias Sanitarias", phone: "061", notes: "Urgencias médicas" },
  { type: "bomberos", name: "Bomberos", phone: "080", notes: "Incendios y rescates" },
  { type: "policia", name: "Policía Nacional", phone: "091", notes: "" },
  { type: "guardia_civil", name: "Guardia Civil", phone: "062", notes: "Zonas rurales" },
];

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function MyEmergencyPlanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("animales");
  
  // Plan data
  const [plan, setPlan] = useState({
    animales: [],
    contactos: [...DEFAULT_EMERGENCY_CONTACTS],
    puntos_reunion: {
      cercano: { nombre: "", direccion: "", notas: "" },
      lejano: { nombre: "", direccion: "", notas: "" },
    },
    evacuacion: {
      ruta_principal: "",
      ruta_alternativa: "",
      destino: "",
      transporte: "",
      tiempo_estimado: "",
      notas: "",
    },
    documentos: {},
    notas_generales: "",
    ultima_revision: null,
  });

  // Dialogs
  const [showAnimalDialog, setShowAnimalDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  // Form states
  const [animalForm, setAnimalForm] = useState({
    nombre: "",
    tipo: "perro",
    raza: "",
    color: "",
    edad: "",
    microchip: "",
    medicacion: "",
    alergias: "",
    veterinario: "",
    notas: "",
    foto_url: "",
  });

  const [contactForm, setContactForm] = useState({
    type: "personal",
    name: "",
    phone: "",
    phone2: "",
    email: "",
    direccion: "",
    notes: "",
  });

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    loadPlan();
  }, [user]);

  const loadPlan = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("ea_token");
      const res = await axios.get(`${API}/prepara/my-plan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.data && Object.keys(res.data).length > 0) {
        setPlan(prev => ({
          ...prev,
          ...res.data,
          contactos: res.data.contactos?.length > 0 
            ? res.data.contactos 
            : [...DEFAULT_EMERGENCY_CONTACTS],
        }));
      }
    } catch (error) {
      console.error("Error loading plan:", error);
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("ea_token");
      await axios.put(
        `${API}/prepara/my-plan`,
        { ...plan, ultima_revision: new Date().toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Plan guardado correctamente");
      setPlan(prev => ({ ...prev, ultima_revision: new Date().toISOString() }));
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error("Error al guardar el plan");
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // ANIMAL MANAGEMENT
  // ============================================
  const openAnimalDialog = (animal = null) => {
    if (animal) {
      setEditingAnimal(animal);
      setAnimalForm(animal);
    } else {
      setEditingAnimal(null);
      setAnimalForm({
        nombre: "",
        tipo: "perro",
        raza: "",
        color: "",
        edad: "",
        microchip: "",
        medicacion: "",
        alergias: "",
        veterinario: "",
        notas: "",
        foto_url: "",
      });
    }
    setShowAnimalDialog(true);
  };

  const saveAnimal = () => {
    if (!animalForm.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    const newAnimal = {
      ...animalForm,
      id: editingAnimal?.id || `animal_${Date.now()}`,
    };

    setPlan(prev => ({
      ...prev,
      animales: editingAnimal
        ? prev.animales.map(a => a.id === editingAnimal.id ? newAnimal : a)
        : [...prev.animales, newAnimal],
    }));

    setShowAnimalDialog(false);
    toast.success(editingAnimal ? "Animal actualizado" : "Animal añadido");
  };

  const deleteAnimal = (animalId) => {
    if (!window.confirm("¿Eliminar este animal del plan?")) return;
    setPlan(prev => ({
      ...prev,
      animales: prev.animales.filter(a => a.id !== animalId),
    }));
    toast.success("Animal eliminado");
  };

  // ============================================
  // CONTACT MANAGEMENT
  // ============================================
  const openContactDialog = (contact = null, index = null) => {
    if (contact) {
      setEditingContact({ ...contact, index });
      setContactForm(contact);
    } else {
      setEditingContact(null);
      setContactForm({
        type: "personal",
        name: "",
        phone: "",
        phone2: "",
        email: "",
        direccion: "",
        notes: "",
      });
    }
    setShowContactDialog(true);
  };

  const saveContact = () => {
    if (!contactForm.name.trim() || !contactForm.phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }

    setPlan(prev => ({
      ...prev,
      contactos: editingContact?.index !== undefined
        ? prev.contactos.map((c, i) => i === editingContact.index ? contactForm : c)
        : [...prev.contactos, contactForm],
    }));

    setShowContactDialog(false);
    toast.success(editingContact ? "Contacto actualizado" : "Contacto añadido");
  };

  const deleteContact = (index) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    setPlan(prev => ({
      ...prev,
      contactos: prev.contactos.filter((_, i) => i !== index),
    }));
    toast.success("Contacto eliminado");
  };

  // ============================================
  // DOCUMENT CHECKLIST
  // ============================================
  const toggleDocument = (docId) => {
    setPlan(prev => ({
      ...prev,
      documentos: {
        ...prev.documentos,
        [docId]: !prev.documentos[docId],
      },
    }));
  };

  const getDocumentProgress = () => {
    const total = DOCUMENTS_CHECKLIST.length;
    const completed = Object.values(plan.documentos).filter(Boolean).length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  };

  // ============================================
  // PLAN COMPLETION
  // ============================================
  const getPlanCompletion = () => {
    let score = 0;
    let maxScore = 0;

    // Animals (30 points)
    maxScore += 30;
    if (plan.animales.length > 0) score += 30;

    // Contacts (20 points)
    maxScore += 20;
    const personalContacts = plan.contactos.filter(c => c.type !== "emergencia" && c.type !== "sanitario");
    if (personalContacts.length >= 2) score += 20;
    else if (personalContacts.length === 1) score += 10;

    // Meeting points (20 points)
    maxScore += 20;
    if (plan.puntos_reunion?.cercano?.direccion) score += 10;
    if (plan.puntos_reunion?.lejano?.direccion) score += 10;

    // Evacuation plan (20 points)
    maxScore += 20;
    if (plan.evacuacion?.ruta_principal) score += 10;
    if (plan.evacuacion?.destino) score += 10;

    // Documents (10 points)
    maxScore += 10;
    const docProgress = getDocumentProgress();
    score += Math.round((docProgress.percent / 100) * 10);

    return Math.round((score / maxScore) * 100);
  };

  // ============================================
  // EXPORT TO PDF
  // ============================================
  const exportToPDF = () => {
    const doc = new jsPDF();
    let yPos = 20;
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);

    // Header
    doc.setFillColor(129, 128, 26);
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("MI PLAN DE EMERGENCIA FAMILIAR", margin, 18);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Emergencia Animal - Nadie se queda atrás", margin, 28);
    
    yPos = 50;

    // Plan completion
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.text(`Plan completado: ${getPlanCompletion()}%`, margin, yPos);
    doc.setFontSize(9);
    const revisionDate = plan.ultima_revision ? new Date(plan.ultima_revision).toLocaleDateString('es-ES') : 'Nunca';
    doc.text(normalizeForPDF(`Última revision: ${revisionDate}`), margin + 80, yPos);
    yPos += 15;

    // SECTION 1: MIS ANIMALES
    doc.setFillColor(245, 158, 11);
    doc.rect(margin - 5, yPos - 5, contentWidth + 10, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("MIS ANIMALES", margin, yPos + 3);
    yPos += 15;

    doc.setTextColor(30, 41, 59);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    if (plan.animales.length === 0) {
      doc.text("No hay animales registrados", margin, yPos);
      yPos += 10;
    } else {
      plan.animales.forEach((animal, idx) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        const animalType = ANIMAL_TYPES.find(t => t.id === animal.tipo);
        doc.setFont("helvetica", "bold");
        doc.text(normalizeForPDF(`${animal.nombre}`), margin, yPos);
        yPos += 5;
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(normalizeForPDF(`Tipo: ${animalType?.label || animal.tipo} | Raza: ${animal.raza || '-'} | Color: ${animal.color || '-'}`), margin + 5, yPos);
        yPos += 4;
        doc.text(normalizeForPDF(`Microchip: ${animal.microchip || 'Sin registrar'}`), margin + 5, yPos);
        yPos += 4;
        if (animal.medicacion) {
          doc.text(normalizeForPDF(`Medicacion: ${animal.medicacion}`), margin + 5, yPos);
          yPos += 4;
        }
        if (animal.alergias) {
          doc.text(normalizeForPDF(`Alergias: ${animal.alergias}`), margin + 5, yPos);
          yPos += 4;
        }
        yPos += 5;
      });
    }

    // SECTION 2: CONTACTOS DE EMERGENCIA
    yPos += 5;
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFillColor(59, 130, 246);
    doc.rect(margin - 5, yPos - 5, contentWidth + 10, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("CONTACTOS DE EMERGENCIA", margin, yPos + 3);
    yPos += 15;

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(9);

    plan.contactos.forEach((contact, idx) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFont("helvetica", "bold");
      doc.text(normalizeForPDF(`${contact.name}:`), margin, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(contact.phone, margin + 50, yPos);
      if (contact.phone2) {
        doc.text(`/ ${contact.phone2}`, margin + 80, yPos);
      }
      yPos += 5;
    });

    // SECTION 3: PUNTOS DE ENCUENTRO
    yPos += 10;
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFillColor(34, 197, 94);
    doc.rect(margin - 5, yPos - 5, contentWidth + 10, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("PUNTOS DE ENCUENTRO", margin, yPos + 3);
    yPos += 15;

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(10);
    
    doc.setFont("helvetica", "bold");
    doc.text("Punto CERCANO (cerca de casa):", margin, yPos);
    yPos += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(normalizeForPDF(plan.puntos_reunion?.cercano?.nombre || "No definido"), margin + 5, yPos);
    yPos += 4;
    doc.text(normalizeForPDF(plan.puntos_reunion?.cercano?.direccion || ""), margin + 5, yPos);
    yPos += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Punto LEJANO (fuera de la zona):", margin, yPos);
    yPos += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(normalizeForPDF(plan.puntos_reunion?.lejano?.nombre || "No definido"), margin + 5, yPos);
    yPos += 4;
    doc.text(normalizeForPDF(plan.puntos_reunion?.lejano?.direccion || ""), margin + 5, yPos);
    yPos += 10;

    // SECTION 4: PLAN DE EVACUACIÓN
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFillColor(239, 68, 68);
    doc.rect(margin - 5, yPos - 5, contentWidth + 10, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("PLAN DE EVACUACION", margin, yPos + 3);
    yPos += 15;

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    const evacInfo = [
      ["Ruta principal:", plan.evacuacion?.ruta_principal || "No definida"],
      ["Ruta alternativa:", plan.evacuacion?.ruta_alternativa || "No definida"],
      ["Destino:", plan.evacuacion?.destino || "No definido"],
      ["Transporte:", plan.evacuacion?.transporte || "No definido"],
      ["Tiempo estimado:", plan.evacuacion?.tiempo_estimado || "No definido"],
    ];

    evacInfo.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, margin, yPos);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(value, contentWidth - 40);
      doc.text(lines, margin + 40, yPos);
      yPos += 5 * lines.length + 2;
    });

    if (plan.evacuacion?.notas) {
      yPos += 3;
      doc.setFont("helvetica", "bold");
      doc.text("Notas:", margin, yPos);
      yPos += 5;
      doc.setFont("helvetica", "normal");
      const notesLines = doc.splitTextToSize(plan.evacuacion.notas, contentWidth - 10);
      doc.text(notesLines, margin + 5, yPos);
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} - Emergencia Animal - IMPRIME Y GUARDA EN LUGAR SEGURO`, margin, 285);

    // Save
    doc.save(`Plan_Emergencia_Familiar_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF descargado - ¡Imprímelo y guárdalo!");
  };

  if (!user) return null;

  const completion = getPlanCompletion();
  const docProgress = getDocumentProgress();

  return (
    <div className="min-h-screen" data-testid="my-emergency-plan-page" data-page="plan" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: "#2D3561", borderBottom: B.border }}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/prepara/panel")}
                className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
                style={{ fontFamily: B.mono, color: "#f8f7f4" }}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                PANEL
              </button>
              <div className="h-5 w-px" style={{ background: "#f8f7f440" }} />
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #f8f7f4" }}>
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-bold" style={{ color: "#f8f7f4" }}>MI PLAN DE EMERGENCIA</h1>
                  <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>PLAN FAMILIAR PERSONALIZADO</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={exportToPDF}
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all"
                style={{ fontFamily: B.mono, color: "#8B9A2D", border: "2px solid #8B9A2D" }}
                data-testid="download-pdf-btn"
              >
                <FileDown className="w-3.5 h-3.5" />
                DESCARGAR PDF
              </button>
              <button
                onClick={savePlan}
                disabled={saving}
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                style={{ fontFamily: B.mono, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                data-testid="save-plan-btn"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? "GUARDANDO..." : "GUARDAR"}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Plan Completion - BRUTALIST */}
        <div className="mb-8 overflow-hidden" style={{ border: B.border, boxShadow: B.shadow, background: "#fff" }}>
          <div className="h-3" style={{ background: "linear-gradient(90deg, #8B9A2D, #a09f2a)" }} />
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                  Tu Plan de Emergencia Familiar
                </h2>
                <p className="text-sm" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                  {plan.ultima_revision 
                    ? `Última revisión: ${new Date(plan.ultima_revision).toLocaleDateString('es-ES')}`
                    : "Nunca revisado - ¡Complétalo hoy!"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold" style={{ fontFamily: B.font, color: "#8B9A2D" }}>{completion}%</p>
                <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>completado</p>
              </div>
            </div>
            <div className="h-3" style={{ background: "#2D356115", border: "2px solid #2D3561" }}>
              <div className="h-full transition-all" style={{ width: `${completion}%`, background: "#686af3" }} />
            </div>
            
            {completion < 100 && (
              <p className="text-xs mt-3" style={{ fontFamily: B.mono, color: "#f0a030" }}>
                Completa todas las secciones para tener un plan efectivo en caso de emergencia
              </p>
            )}
          </div>
        </div>

        {/* Section Navigation - BRUTALIST */}
        <div className="grid grid-cols-5 gap-3 mb-8">
          {PLAN_SECTIONS.map((section) => {
            const SectionIcon = section.icon;
            const isActive = activeSection === section.id;
            
            return (
              <div
                key={section.id}
                className="cursor-pointer transition-all hover:-translate-y-0.5"
                onClick={() => setActiveSection(section.id)}
                data-testid={`section-${section.id}`}
                style={{
                  border: B.border,
                  boxShadow: isActive ? B.shadowSm : "none",
                  background: isActive ? "#fff" : "#f8f7f4",
                  borderTop: isActive ? `4px solid ${section.color}` : B.border,
                }}
              >
                <div className="p-4 text-center">
                  <div 
                    className="w-10 h-10 mx-auto mb-2 flex items-center justify-center"
                    style={{ background: `${section.color}20`, border: `2px solid ${section.color}` }}
                  >
                    <SectionIcon className="w-5 h-5" style={{ color: section.color }} />
                  </div>
                  <h3 className="text-xs font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                    {section.label}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* Section Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {/* ========== MIS ANIMALES ========== */}
            {activeSection === "animales" && (
              <div style={{ border: B.border, background: "#fff" }}>
                <div className="p-6 flex items-center justify-between" style={{ borderBottom: "2px solid #2D3561" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: "#f0a03020", border: "2px solid #f0a030" }}>
                      <PawPrint className="w-5 h-5" style={{ color: "#f0a030" }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>Mis Animales</h3>
                      <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                        Registra todos tus animales con sus datos importantes
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => openAnimalDialog()}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all hover:-translate-y-0.5"
                    style={{ fontFamily: B.mono, background: "#f0a030", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                    data-testid="add-animal-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Añadir Animal
                  </button>
                </div>
                <div className="p-6">
                  {plan.animales.length === 0 ? (
                    <div className="text-center py-12">
                      <PawPrint className="w-12 h-12 mx-auto mb-3" style={{ color: "#2D356133" }} />
                      <p className="text-sm" style={{ fontFamily: B.font, color: "#2D356188" }}>No tienes animales registrados</p>
                      <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356155" }}>Añade tus animales para tener su información lista</p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {plan.animales.map((animal) => {
                        const animalType = ANIMAL_TYPES.find(t => t.id === animal.tipo);
                        return (
                          <div key={animal.id} className="p-4" style={{ border: "2px solid #f0a03044", background: "#fffcf5" }}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-4">
                                <div className="w-16 h-16 flex items-center justify-center text-3xl" style={{ background: "#fff", border: "2px solid #f0a030" }}>
                                  {animalType?.icon || ""}
                                </div>
                                <div>
                                  <h3 className="text-lg font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                                    {animal.nombre}
                                  </h3>
                                  <p className="text-sm" style={{ color: "#2D356188" }}>
                                    {animalType?.label} {animal.raza && `· ${animal.raza}`} {animal.color && `· ${animal.color}`}
                                  </p>
                                  {animal.microchip && (
                                    <p className="text-xs mt-1" style={{ fontFamily: B.mono, color: "#f0a030" }}>
                                      Microchip: {animal.microchip}
                                    </p>
                                  )}
                                  {animal.medicacion && (
                                    <p className="text-xs mt-1" style={{ color: "#e8637a" }}>
                                      Medicación: {animal.medicacion}
                                    </p>
                                  )}
                                  {animal.alergias && (
                                    <p className="text-xs mt-1" style={{ color: "#f64e0e" }}>
                                      Alergias: {animal.alergias}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openAnimalDialog(animal)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteAnimal(animal.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ========== CONTACTOS ========== */}
            {activeSection === "contactos" && (
              <div style={{ border: B.border, background: "#fff" }}>
                <div className="p-6 flex items-center justify-between" style={{ borderBottom: "2px solid #2D3561" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: "#686af320", border: "2px solid #686af3" }}>
                      <Phone className="w-5 h-5" style={{ color: "#686af3" }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>Contactos de Emergencia</h3>
                      <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>Teléfonos importantes para ti y tu familia</p>
                    </div>
                  </div>
                  <button
                    onClick={() => openContactDialog()}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all hover:-translate-y-0.5"
                    style={{ fontFamily: B.mono, background: "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                    data-testid="add-contact-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Añadir Contacto
                  </button>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {plan.contactos.map((contact, idx) => {
                      const isOfficial = ["emergencia", "sanitario", "bomberos", "policia", "guardia_civil"].includes(contact.type);
                      return (
                        <div key={idx} className="flex items-center justify-between p-4"
                          style={{ border: `2px solid ${isOfficial ? "#e8637a44" : "#686af344"}`, background: isOfficial ? "#fff4f4" : "#f0f0ff" }}>
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 flex items-center justify-center" style={{ background: isOfficial ? "#e8637a20" : "#686af320", border: `2px solid ${isOfficial ? "#e8637a" : "#686af3"}` }}>
                              <Phone className="w-5 h-5" style={{ color: isOfficial ? "#e8637a" : "#686af3" }} />
                            </div>
                            <div>
                              <h4 className="text-sm font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>{contact.name}</h4>
                              <p className="text-lg font-bold" style={{ fontFamily: B.mono, color: "#686af3" }}>
                                {contact.phone}
                                {contact.phone2 && <span className="text-sm ml-2" style={{ color: "#2D356188" }}>/ {contact.phone2}</span>}
                              </p>
                              {contact.notes && <p className="text-xs" style={{ color: "#2D356188" }}>{contact.notes}</p>}
                            </div>
                          </div>
                          {!isOfficial && (
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openContactDialog(contact, idx)}><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteContact(idx)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 p-4" style={{ borderLeft: "4px solid #686af3", background: "#686af310" }}>
                    <p className="text-sm" style={{ color: "#2D3561" }}>
                      <strong>Tip:</strong> Añade al menos 2 contactos personales (veterinario, familiar fuera de la zona, vecino de confianza) además de los números de emergencia.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ========== PUNTOS DE ENCUENTRO ========== */}
            {activeSection === "reuniones" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle>Puntos de Encuentro</CardTitle>
                      <CardDescription>
                        Define dónde reunirte con tu familia si os separáis
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Punto Cercano */}
                  <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className="bg-green-500">CERCANO</Badge>
                      <span className="font-body text-sm text-slate-600">
                        Cerca de casa, accesible a pie
                      </span>
                    </div>
                    <div className="grid gap-4">
                      <div>
                        <Label className="font-heading text-sm">Nombre del lugar</Label>
                        <Input
                          value={plan.puntos_reunion?.cercano?.nombre || ""}
                          onChange={(e) => setPlan(prev => ({
                            ...prev,
                            puntos_reunion: {
                              ...prev.puntos_reunion,
                              cercano: { ...prev.puntos_reunion?.cercano, nombre: e.target.value }
                            }
                          }))}
                          placeholder="Ej: Plaza del barrio, entrada del parque..."
                        />
                      </div>
                      <div>
                        <Label className="font-heading text-sm">Dirección exacta</Label>
                        <Input
                          value={plan.puntos_reunion?.cercano?.direccion || ""}
                          onChange={(e) => setPlan(prev => ({
                            ...prev,
                            puntos_reunion: {
                              ...prev.puntos_reunion,
                              cercano: { ...prev.puntos_reunion?.cercano, direccion: e.target.value }
                            }
                          }))}
                          placeholder="Calle, número, referencias..."
                        />
                      </div>
                      <div>
                        <Label className="font-heading text-sm">Notas adicionales</Label>
                        <Textarea
                          value={plan.puntos_reunion?.cercano?.notas || ""}
                          onChange={(e) => setPlan(prev => ({
                            ...prev,
                            puntos_reunion: {
                              ...prev.puntos_reunion,
                              cercano: { ...prev.puntos_reunion?.cercano, notas: e.target.value }
                            }
                          }))}
                          placeholder="Instrucciones específicas, señales de reconocimiento..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Punto Lejano */}
                  <div className="p-6 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className="bg-amber-500">LEJANO</Badge>
                      <span className="font-body text-sm text-slate-600">
                        Fuera de la zona de peligro, requiere transporte
                      </span>
                    </div>
                    <div className="grid gap-4">
                      <div>
                        <Label className="font-heading text-sm">Nombre del lugar</Label>
                        <Input
                          value={plan.puntos_reunion?.lejano?.nombre || ""}
                          onChange={(e) => setPlan(prev => ({
                            ...prev,
                            puntos_reunion: {
                              ...prev.puntos_reunion,
                              lejano: { ...prev.puntos_reunion?.lejano, nombre: e.target.value }
                            }
                          }))}
                          placeholder="Ej: Casa de los abuelos, hotel en otra ciudad..."
                        />
                      </div>
                      <div>
                        <Label className="font-heading text-sm">Dirección completa</Label>
                        <Input
                          value={plan.puntos_reunion?.lejano?.direccion || ""}
                          onChange={(e) => setPlan(prev => ({
                            ...prev,
                            puntos_reunion: {
                              ...prev.puntos_reunion,
                              lejano: { ...prev.puntos_reunion?.lejano, direccion: e.target.value }
                            }
                          }))}
                          placeholder="Dirección completa con ciudad..."
                        />
                      </div>
                      <div>
                        <Label className="font-heading text-sm">Notas</Label>
                        <Textarea
                          value={plan.puntos_reunion?.lejano?.notas || ""}
                          onChange={(e) => setPlan(prev => ({
                            ...prev,
                            puntos_reunion: {
                              ...prev.puntos_reunion,
                              lejano: { ...prev.puntos_reunion?.lejano, notas: e.target.value }
                            }
                          }))}
                          placeholder="Cómo llegar, contacto del lugar..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ========== PLAN DE EVACUACIÓN ========== */}
            {activeSection === "evacuacion" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <Car className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <CardTitle>Plan de Evacuación</CardTitle>
                      <CardDescription>
                        Cómo salir de tu zona en caso de emergencia
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-heading text-sm">Ruta principal</Label>
                      <Input
                        value={plan.evacuacion?.ruta_principal || ""}
                        onChange={(e) => setPlan(prev => ({
                          ...prev,
                          evacuacion: { ...prev.evacuacion, ruta_principal: e.target.value }
                        }))}
                        placeholder="Ej: Salir por A-7 dirección norte..."
                      />
                    </div>
                    <div>
                      <Label className="font-heading text-sm">Ruta alternativa</Label>
                      <Input
                        value={plan.evacuacion?.ruta_alternativa || ""}
                        onChange={(e) => setPlan(prev => ({
                          ...prev,
                          evacuacion: { ...prev.evacuacion, ruta_alternativa: e.target.value }
                        }))}
                        placeholder="Si la principal está bloqueada..."
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-heading text-sm">Destino de evacuación</Label>
                      <Input
                        value={plan.evacuacion?.destino || ""}
                        onChange={(e) => setPlan(prev => ({
                          ...prev,
                          evacuacion: { ...prev.evacuacion, destino: e.target.value }
                        }))}
                        placeholder="Casa de familiares, hotel, refugio..."
                      />
                    </div>
                    <div>
                      <Label className="font-heading text-sm">Medio de transporte</Label>
                      <Select
                        value={plan.evacuacion?.transporte || ""}
                        onValueChange={(value) => setPlan(prev => ({
                          ...prev,
                          evacuacion: { ...prev.evacuacion, transporte: value }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="coche">Coche propio</SelectItem>
                          <SelectItem value="moto">Moto</SelectItem>
                          <SelectItem value="transporte_publico">Transporte público</SelectItem>
                          <SelectItem value="a_pie">A pie</SelectItem>
                          <SelectItem value="vecino">Con vecinos</SelectItem>
                          <SelectItem value="emergencias">Servicios de emergencia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="font-heading text-sm">Tiempo estimado de evacuación</Label>
                    <Input
                      value={plan.evacuacion?.tiempo_estimado || ""}
                      onChange={(e) => setPlan(prev => ({
                        ...prev,
                        evacuacion: { ...prev.evacuacion, tiempo_estimado: e.target.value }
                      }))}
                      placeholder="Ej: 45 minutos hasta destino seguro"
                    />
                  </div>

                  <div>
                    <Label className="font-heading text-sm">Notas importantes</Label>
                    <Textarea
                      value={plan.evacuacion?.notas || ""}
                      onChange={(e) => setPlan(prev => ({
                        ...prev,
                        evacuacion: { ...prev.evacuacion, notas: e.target.value }
                      }))}
                      placeholder="Puntos de recarga de gasolina, paradas necesarias para animales, personas que necesitan ayuda especial..."
                      rows={4}
                    />
                  </div>

                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4">
                      <h4 className="font-heading text-sm font-bold text-red-800 mb-2">
                        Recuerda
                      </h4>
                      <ul className="font-body text-sm text-red-700 space-y-1">
                        <li>• Practica la ruta de evacuación con tus animales</li>
                        <li>• Ten el depósito del coche siempre al menos medio lleno</li>
                        <li>• Infórmate sobre refugios que admitan animales en tu ruta</li>
                        <li>• NUNCA dejes a tus animales atrás</li>
                      </ul>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            )}

            {/* ========== DOCUMENTOS ========== */}
            {activeSection === "documentos" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle>Documentos Importantes</CardTitle>
                        <CardDescription>
                          Asegúrate de tener toda la documentación lista
                        </CardDescription>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-2xl font-bold text-purple-600">
                        {docProgress.completed}/{docProgress.total}
                      </p>
                      <p className="font-body text-xs text-slate-500">documentos listos</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Progress value={docProgress.percent} className="mb-6 h-3" />
                  
                  <div className="space-y-3">
                    {DOCUMENTS_CHECKLIST.map((doc) => (
                      <div
                        key={doc.id}
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          plan.documentos[doc.id]
                            ? "bg-green-50 border-green-200"
                            : doc.essential
                            ? "bg-amber-50 border-amber-200"
                            : "bg-white border-slate-200"
                        }`}
                        onClick={() => toggleDocument(doc.id)}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          plan.documentos[doc.id]
                            ? "bg-green-500"
                            : "bg-slate-200"
                        }`}>
                          {plan.documentos[doc.id] && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`font-body text-sm ${
                            plan.documentos[doc.id] ? "line-through text-slate-400" : "text-[var(--navy)]"
                          }`}>
                            {doc.name}
                          </p>
                        </div>
                        {doc.essential && !plan.documentos[doc.id] && (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">
                            Esencial
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>

                  <Card className="mt-6 bg-purple-50 border-purple-200">
                    <CardContent className="p-4">
                      <p className="font-body text-sm text-purple-800">
                        <strong>Tip:</strong> Guarda copias en una bolsa impermeable dentro de tu mochila de evacuación,
                        y otra copia en la nube (email, Google Drive).
                      </p>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* ========== ANIMAL DIALOG ========== */}
      <Dialog open={showAnimalDialog} onOpenChange={setShowAnimalDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnimal ? "Editar Animal" : "Añadir Animal"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={animalForm.nombre}
                  onChange={(e) => setAnimalForm(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Nombre del animal"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select
                  value={animalForm.tipo}
                  onValueChange={(value) => setAnimalForm(prev => ({ ...prev, tipo: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ANIMAL_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Raza</Label>
                <Input
                  value={animalForm.raza}
                  onChange={(e) => setAnimalForm(prev => ({ ...prev, raza: e.target.value }))}
                  placeholder="Raza o mezcla"
                />
              </div>
              <div>
                <Label>Color/Características</Label>
                <Input
                  value={animalForm.color}
                  onChange={(e) => setAnimalForm(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="Color, marcas..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Edad</Label>
                <Input
                  value={animalForm.edad}
                  onChange={(e) => setAnimalForm(prev => ({ ...prev, edad: e.target.value }))}
                  placeholder="Ej: 3 años"
                />
              </div>
              <div>
                <Label>Número de Microchip</Label>
                <Input
                  value={animalForm.microchip}
                  onChange={(e) => setAnimalForm(prev => ({ ...prev, microchip: e.target.value }))}
                  placeholder="15 dígitos"
                />
              </div>
            </div>

            <div>
              <Label>Medicación habitual</Label>
              <Textarea
                value={animalForm.medicacion}
                onChange={(e) => setAnimalForm(prev => ({ ...prev, medicacion: e.target.value }))}
                placeholder="Medicamentos, dosis, frecuencia..."
                rows={2}
              />
            </div>

            <div>
              <Label>Alergias / Condiciones especiales</Label>
              <Input
                value={animalForm.alergias}
                onChange={(e) => setAnimalForm(prev => ({ ...prev, alergias: e.target.value }))}
                placeholder="Alergias alimentarias, condiciones médicas..."
              />
            </div>

            <div>
              <Label>Veterinario habitual</Label>
              <Input
                value={animalForm.veterinario}
                onChange={(e) => setAnimalForm(prev => ({ ...prev, veterinario: e.target.value }))}
                placeholder="Nombre y teléfono"
              />
            </div>

            <div>
              <Label>Notas adicionales</Label>
              <Textarea
                value={animalForm.notas}
                onChange={(e) => setAnimalForm(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Comportamiento, miedos, necesidades especiales..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnimalDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={saveAnimal}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {editingAnimal ? "Guardar cambios" : "Añadir animal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== CONTACT DIALOG ========== */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? "Editar Contacto" : "Añadir Contacto"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Tipo de contacto</Label>
              <Select
                value={contactForm.type}
                onValueChange={(value) => setContactForm(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="veterinario">Veterinario</SelectItem>
                  <SelectItem value="familiar">Familiar</SelectItem>
                  <SelectItem value="vecino">Vecino</SelectItem>
                  <SelectItem value="refugio">Refugio/Protectora</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Nombre *</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre del contacto"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono principal *</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="600 000 000"
                />
              </div>
              <div>
                <Label>Teléfono alternativo</Label>
                <Input
                  value={contactForm.phone2}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone2: e.target.value }))}
                  placeholder="Opcional"
                />
              </div>
            </div>

            <div>
              <Label>Notas</Label>
              <Input
                value={contactForm.notes}
                onChange={(e) => setContactForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Horarios, relación, etc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={saveContact}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {editingContact ? "Guardar cambios" : "Añadir contacto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
